/**
 * src/api/printing/PrintingDataApi.ts — Accès données DB pour le module impression.
 *
 * Centralise les requêtes Supabase du print-orchestrator (invariant 4.B.2).
 * Seul point de contact Supabase pour les données d'impression de tickets (hors
 * config imprimantes qui reste dans PrinterConfigApi).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 *
 * Invariants :
 *   4.B.2  — seul fichier autorisé pour ces requêtes hors couche UI
 *   4.A.10 — tenant_id résolu par RLS côté serveur (pas dans le body)
 *   4.A.15 — aucun champ PII loggé dans ce module
 *   4.B.4  — 0 any
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase } from '@/api/errors';
import type { AppError } from '@/api/errors';
import type { Database } from '@/integrations/supabase/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SaleRow = Database['public']['Tables']['sales']['Row'];
type SaleItemRow = Database['public']['Tables']['sale_items']['Row'];
type CreditNoteRow = Database['public']['Tables']['credit_notes']['Row'];
type CreditNoteItemRow = Database['public']['Tables']['credit_note_items']['Row'];

/** Sous-ensemble produit pour le ticket de démo (select 'name, price, tva_rate'). */
export type ProductSample = { name: string; price: number; tva_rate: number | null };

/** Champs tenant nécessaires au pied de ticket (slug pour QR Google Review). */
export type TenantSlugAndReview = { slug: string | null; google_review_url: string | null };

// ---------------------------------------------------------------------------
// PrintingDataApi
// ---------------------------------------------------------------------------

export const PrintingDataApi = {
  /**
   * Charge la vente complète pour impression ticket (Vague 1 de printSaleReceipt).
   *
   * @param saleId UUID Postgres de la vente.
   */
  async getSale(saleId: string): Promise<Result<SaleRow | null, AppError>> {
    const { data, error } = await supabase.from('sales').select('*').eq('id', saleId).maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getSale'));
    return ok(data);
  },

  /**
   * Charge les lignes d'une vente pour impression ticket (Vague 1).
   *
   * @param saleId UUID Postgres de la vente.
   */
  async getSaleItems(saleId: string): Promise<Result<SaleItemRow[], AppError>> {
    const { data, error } = await supabase.from('sale_items').select('*').eq('sale_id', saleId);
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getSaleItems'));
    return ok(data ?? []);
  },

  /**
   * Charge un avoir pour impression du ticket de remboursement.
   *
   * @param creditNoteId UUID Postgres de l'avoir.
   */
  async getCreditNote(creditNoteId: string): Promise<Result<CreditNoteRow | null, AppError>> {
    const { data, error } = await supabase
      .from('credit_notes')
      .select('*')
      .eq('id', creditNoteId)
      .maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getCreditNote'));
    return ok(data);
  },

  /** Charge les lignes d'un avoir pour impression du ticket de remboursement. */
  async getCreditNoteItems(creditNoteId: string): Promise<Result<CreditNoteItemRow[], AppError>> {
    const { data, error } = await supabase
      .from('credit_note_items')
      .select('*')
      .eq('credit_note_id', creditNoteId);
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getCreditNoteItems'));
    return ok(data ?? []);
  },

  /**
   * Charge le slug et l'URL Google Review du tenant pour le QR code auto
   * en pied de ticket (Vague 1 de printSaleReceipt).
   *
   * tenant_id passé explicitement car le caller connaît déjà l'id (lecture
   * directe par clé primaire, pas de jointure via get_my_tenant_id).
   *
   * @param tenantId UUID du tenant.
   */
  async getTenantSlugAndReview(
    tenantId: string,
  ): Promise<Result<TenantSlugAndReview | null, AppError>> {
    const { data, error } = await supabase
      .from('tenants')
      .select('slug, google_review_url')
      .eq('id', tenantId)
      .maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getTenantSlugAndReview'));
    return ok(data as TenantSlugAndReview | null);
  },

  /**
   * Charge le display_name du caissier pour l'en-tête du ticket
   * (Vague 2 de printSaleReceipt).
   *
   * PII : display_name est un alias staff (pas email). Invariant 4.A.15.
   *
   * @param userId UUID de l'utilisateur (sales.user_id).
   */
  async getCashierName(userId: string): Promise<Result<string | null, AppError>> {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getCashierName'));
    return ok((data as { display_name?: string | null } | null)?.display_name ?? null);
  },

  /**
   * Charge le nom de la caisse pour l'en-tête du ticket
   * (Vague 2 de printSaleReceipt).
   *
   * @param registerId UUID de la caisse (sales.register_id).
   */
  async getRegisterName(registerId: string): Promise<Result<string | null, AppError>> {
    const { data, error } = await supabase
      .from('registers')
      .select('name')
      .eq('id', registerId)
      .maybeSingle();
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getRegisterName'));
    return ok((data as { name?: string | null } | null)?.name ?? null);
  },

  /**
   * Charge un échantillon de produits actifs pour le ticket de démo.
   * Utilisé par buildDemoTicketData (preview page Personnalisation).
   *
   * @param tenantId UUID du tenant.
   * @param limit    Nombre max de produits à charger.
   */
  async getProductSamples(
    tenantId: string,
    limit: number,
  ): Promise<Result<ProductSample[] | null, AppError>> {
    const { data, error } = await supabase
      .from('products')
      .select('name, price, tva_rate')
      .eq('tenant_id', tenantId)
      .eq('active', true)
      .order('name')
      .limit(limit);
    if (error) return err(appErrorFromSupabase(error, 'PrintingDataApi.getProductSamples'));
    return ok(data as ProductSample[] | null);
  },

  /**
   * Réglage du ticket tenant_feature_toggles (ex. retour_tpe_detaille). Flag opt-in : sans ligne = false.
   * Erreur de lecture = false (le ticket garde son format historique).
   */
  async isOptInFeatureEnabled(tenantId: string, featureKey: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('tenant_feature_toggles')
      .select('enabled')
      .eq('tenant_id', tenantId)
      .eq('feature_key', featureKey)
      .maybeSingle();
    if (error || !data) return false;
    return data.enabled === true;
  },
};
